/**
 * LOGOI LAB - Layer 5: Penrose Quantum Coherence
 * Computes system coherence and entropy
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, CoherenceResult } from '../types';
import { DOMAINS, COLORS } from '../domain/constants';
import { clamp, harmonicMean, variance } from '../utils/math';

/**
 * Compute Penrose Coherence
 * 
 * Uses harmonic mean to calculate coherence across 7 life domains.
 * A single low domain drags down entire coherence (the limiting factor).
 * Λ modulates coherence: alignment creates coherence.
 * 
 * @param s - Current consciousness state
 * @param Lambda - Current Logos alignment value
 * @returns CoherenceResult with coherence value and domain breakdown
 */
export function computeCoherence(s: ConsciousnessState, Lambda: number): CoherenceResult {
  const state = s as any;
  
  // Calculate domain averages
  const physical  = (state.sleep + state.nutrition + state.exercise + state.energy) / 4;
  const emotional = (state.peace + state.gratitude + state.love + state.joy) / 4;
  const mental    = (state.clarity + state.focus + state.creativity + state.wisdom) / 4;
  const spiritual = (state.faith + state.meditation + state.service + state.presence) / 4;
  const relational= (state.family + state.friendship + state.community + state.compassion) / 4;
  const purpose   = (state.meaning + state.mission + state.contribution + state.legacy) / 4;
  const alimento  = ((state.nourishment ?? 0.5) + (state.taste_presence ?? 0.5) + (state.food_harmony ?? 0.5) + (state.gut_resonance ?? 0.5)) / 4;
  
  const vals = [physical, emotional, mental, spiritual, relational, purpose, alimento];
  
  // Harmonic mean: a single low value drags down the result
  const rawCoherence = harmonicMean(vals);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const varianceVal = vals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / vals.length;
  
  // Λ modulates coherence: alignment creates coherence
  const lambdaBoost = Lambda * 0.4;
  const coherence = clamp(rawCoherence * (0.7 + lambdaBoost));
  
  return {
    coherence,
    rawCoherence,
    entropy: clamp(Math.sqrt(varianceVal) * 3),
    domains: { physical, emotional, mental, spiritual, relational, purpose, alimento },
    lambdaBoost,
    breakdown: [
      { label: "Cuerpo", value: physical, color: DOMAINS.physical.color },
      { label: "Emociones", value: emotional, color: DOMAINS.emotional.color },
      { label: "Mente", value: mental, color: DOMAINS.mental.color },
      { label: "Espíritu", value: spiritual, color: DOMAINS.spiritual.color },
      { label: "Relaciones", value: relational, color: DOMAINS.relational.color },
      { label: "Propósito", value: purpose, color: DOMAINS.purpose.color },
      { label: "Alimento", value: alimento, color: DOMAINS.alimento.color },
    ]
  };
}
